import React, { useContext } from 'react';
import Canvas from '../../components/Canvas/Canvas';
import CheckboxInput from '../../components/Inputs/CheckboxInput';
import DateInput from '../../components/Inputs/DateInput';
import FileInput from '../../components/Inputs/FileInput';
import RadioInput from '../../components/Inputs/RadioInput';
import SelectInput from '../../components/Inputs/SelectInput';
import TextareaInput from '../../components/Inputs/TextareaInput';
import TextInput from '../../components/Inputs/TextInput';
import {
	checkFilenameLength,
	getFutureDate,
	parseFileSize,
	parseToOptions,
	normalizeDrawingToolImgSrc,
} from '../../helpers';
import { FileIcon, TrashIcon } from '../../components/SVGIcons/SVGIcons';
import { CurrentTeamContext } from '../../contexts/CurrentTeamContext';

export default function DocketSwitch(props) {
	const {
		field,
		handleFormSelectData,
		handleFormData,
		customErrors = {},
		formData = {},
		formFiles = {},
		handleFocused,
		focused,
		handleFiles,
		parentOrder = {},
		autofill,
		dentallyData,
		supplier,
	} = props || {};

	const { teamState } = useContext(CurrentTeamContext);

	if (!field?.data) return null;

	const fieldName = field.data.fieldName;
	const fieldText = field.data.fieldText || fieldName;

	const formField = formData?.fields?.[fieldName];
	const fieldValue = formField?.value;

	const baseMinDays = Number(field?.data?.minDays) ? Number(field.data.minDays) : 0;

	const parentCustomField =
		parentOrder.customFields && parentOrder.customFields[fieldName]
			? parentOrder.customFields[fieldName]
			: undefined;

	const buyerTeamUuid = formData?.onBehalfOfBuyerTeam?.value || teamState?.currentTeam?.teamUuid;

	const supplierMinDays = (() => {
		if (!supplier || !buyerTeamUuid) return null;
		const settings = supplier.customerMinDaysSettings;
		if (!Array.isArray(settings)) return null;

		const entry = settings.find((e) => String(e.customerTeamUuid) === String(buyerTeamUuid));
		if (!entry) return null;

		return typeof entry.minimumDays === 'number' ? entry.minimumDays : null;
	})();

	const useCustomerTeamMinDays = field?.data?.useCustomerTeamMinDays === true;

	const effectiveMinDays = useCustomerTeamMinDays && supplierMinDays != null ? supplierMinDays : baseMinDays;

	return (
		<>
			{(() => {
				switch (field?.type) {
					case 'select': {
						const options = parseToOptions(field?.data?.selectOptions, 'docketSelect');

						return (
							<SelectInput
								options={options}
								callback={handleFormSelectData}
								text={fieldText}
								name={fieldName}
								value={fieldValue}
								customError={customErrors[fieldName]}
								required={field?.data?.required}
								customCallbackValue={parentCustomField}
								autofill={autofill}
							/>
						);
					}
					case 'input.text':
						return (
							<TextInput
								text={fieldText}
								name={fieldName}
								placeholder={field?.data?.placeholder}
								defaultValue={fieldValue}
								value={fieldValue}
								type='text'
								callback={handleFormData}
								required={field?.data?.required}
								customError={customErrors[fieldName]}
								customCallbackValue={parentCustomField}
								autofill={autofill}
								options={dentallyData}
								controlled={!!dentallyData}
							/>
						);
					case 'input.file':
						return (
							<>
								<FileInput
									text={fieldText}
									name={fieldName}
									customError={customErrors[fieldName]}
									btnText='Choose file'
									callback={handleFiles}
								/>
								{formFiles && formFiles[fieldName] ? (
									<div
										className='fileCont marginBottom-20'
										style={{ width: '100%', maxWidth: '480px' }}>
										<FileIcon iconClass='placeholder' />
										<div className='fileRow'>
											<div>
												<h4 className='text'>
													{checkFilenameLength(formFiles[fieldName]?.file?.name, 20)}
												</h4>
												<h4 className='text'>
													{parseFileSize(formFiles[fieldName]?.file?.size)}
												</h4>
											</div>
											<TrashIcon
												iconClass='trashIcon'
												onClick={() => handleFiles(null, fieldName)}
											/>
										</div>
									</div>
								) : null}
							</>
						);
					case 'input.phone':
						return (
							<TextInput
								text={fieldText}
								name={fieldName}
								placeholder={field?.data?.placeholder}
								defaultValue={fieldValue}
								type='phone'
								callback={handleFormData}
								required={field?.data?.required}
								customError={customErrors[fieldName]}
								customCallbackValue={parentCustomField}
								autofill={autofill}
							/>
						);
					case 'textarea':
						return (
							<TextareaInput
								text={fieldText}
								name={fieldName}
								placeholder={field?.data?.placeholder}
								defaultValue={fieldValue}
								callback={handleFormData}
								required={field?.data?.required}
								customError={customErrors[fieldName]}
								customCallbackValue={parentCustomField}
								autofill={autofill}
							/>
						);
					case 'input.date':
						return (
							<DateInput
								text={fieldText}
								name={fieldName}
								callback={(value, name) => {
									handleFormData(value, name);
								}}
								handleFocused={handleFocused}
								required={field?.data?.required}
								customError={customErrors[fieldName]}
								focused={focused}
								defaultValue={fieldValue}
								min={new Date(getFutureDate(effectiveMinDays))}
								customCallbackValue={parentCustomField}
								filter={{
									excludeWeekends: field?.data?.excludeWeekends,
									excludeHolidays: field?.data?.excludeHolidays,
								}}
								autofill={autofill}
								dateFormat={field?.data?.timeSelect === true ? 'dd/MM/yyyy hh:mm aa' : 'dd/MM/yyyy'}
								timeSelect={field?.data?.timeSelect}
							/>
						);
					case 'input.checkbox':
						return (
							<CheckboxInput
								text={fieldText}
								name={fieldName}
								checked={!!fieldValue}
								callback={handleFormData}
								required={field?.data?.required}
								customError={customErrors[fieldName]}
								customCallbackValue={parentCustomField}
								autofill={autofill}
							/>
						);
					case 'input.radio': {
						const radios = [];
						for (let radio in field?.data) {
							if (radio !== 'extraRadios' && radio !== 'fieldName' && radio !== 'fieldText') {
								radios.push({ text: field?.data[radio], value: field?.data[radio] });
							}
						}
						return (
							<RadioInput
								text={fieldText}
								name={fieldName}
								defaultValue={fieldValue}
								callback={handleFormData}
								customError={customErrors[fieldName]}
								customCallbackValue={parentCustomField}
								radios={radios}
							/>
						);
					}
					case 'header':
						return <h3>{field?.data?.header}</h3>;
					case 'paragraph':
						return <p>{field?.data?.paragraph}</p>;
					case 'drawingTool': {
						const imgSrc = normalizeDrawingToolImgSrc(field?.data?.drawToolImg?.data);

						return (
							<div className='formRowCenter'>
								<h3 className='header'>{fieldText}</h3>
								<Canvas
									cWidth={500}
									cHeight={700}
									handleFile={handleFiles}
									name={fieldName}
									img={imgSrc}
								/>
							</div>
						);
					}
					case 'graphic':
						return <img className='graphic' src={field?.data?.graphic?.data} alt='Graphic' />;
					default:
						return null;
				}
			})()}
		</>
	);
}
